/**
 * useImageUpload Hook
 *
 * Custom hook for uploading images to Firebase Storage.
 * Handles validation, progress tracking, and storage path management.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - User-scoped storage paths
 * - 10MB file size limit
 * - Storage quota validation
 * - NO localStorage
 */

import { useState } from 'react';
import { ref, uploadBytes, getDownloadURL, UploadMetadata } from 'firebase/storage';
import { storage } from '@/firebaseConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { validateImageFile, validateStorageQuota } from '@/utils/validation';

interface UseImageUploadReturn {
  uploadImage: (file: File) => Promise<UploadResult>;
  uploading: boolean;
  progress: number;
  error: string | null;
}

interface UploadResult {
  downloadUrl: string;
  storagePath: string;
  fileName: string;
}

/**
 * Custom hook for image upload functionality
 *
 * @returns Upload function, uploading state, progress, and error
 */
export function useImageUpload(): UseImageUploadReturn {
  const { currentUser } = useAuth();
  const { storageUsed, storageLimit } = useCredits();
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  /**
   * Upload image to Firebase Storage
   *
   * @param file - Image file to upload
   * @returns Upload result with download URL and storage path
   */
  const uploadImage = async (file: File): Promise<UploadResult> => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Reset state
    setUploading(true);
    setProgress(0);
    setError(null);

    // Check for mock upload (E2E testing)
    if ((window as unknown as { __MOCK_UPLOAD__: boolean }).__MOCK_UPLOAD__) {
      console.log('useImageUpload: Mocking upload');
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
      setUploading(false);
      setProgress(100);
      return {
        downloadUrl: 'https://example.com/mock-image.jpg',
        storagePath: 'mock/path/image.jpg',
        fileName: 'mock-image.jpg'
      };
    }

    try {
      // Validate file
      const fileError = validateImageFile(file);
      if (fileError) {
        setError(fileError);
        throw new Error(fileError);
      }

      // Validate storage quota
      const quotaError = validateStorageQuota(storageUsed, storageLimit, file.size);
      if (quotaError) {
        setError(quotaError);
        throw new Error(quotaError);
      }

      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;
      const storagePath = `uploads/${currentUser.uid}/${fileName}`;

      // Create storage reference
      const storageRef = ref(storage, storagePath);

      // Set metadata
      const metadata: UploadMetadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: currentUser.uid,
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
        },
      };

      // Upload file
      setProgress(50); // Simplified progress (no streaming progress in basic uploadBytes)
      await uploadBytes(storageRef, file, metadata);
      setProgress(75);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);
      setProgress(100);

      setUploading(false);
      setError(null);

      return {
        downloadUrl,
        storagePath,
        fileName,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      setUploading(false);
      setProgress(0);
      throw err;
    }
  };

  return {
    uploadImage,
    uploading,
    progress,
    error,
  };
}
